/**
 * AI Tutor REST client (M4). SERVER-ONLY data (no PowerSync): via
 * `@/lib/rest`'s shared transport, wrapped in TanStack Query. Mirror of
 * `handlers/ai_tutor.rs`'s DTOs.
 */
import { createSafeQuery } from "@/lib/api";
import { createMutation } from "@tanstack/solid-query";
import { apiJson } from "@/lib/rest";

const BASE = "/api/ai-tutor";

// ─── Types (mirror of ai_tutor.rs's DTOs) ──────────────────────────────────

export interface UsageDto {
  inputTokens: number;
  outputTokens: number;
}

export interface ChatResponse {
  conversationId: string;
  isNew: boolean;
  reply: string;
  usage: UsageDto;
  draft: boolean;
}

export interface ConversationSummary {
  id: string;
  kind: "chat" | "submission_feedback";
  title: string | null;
  courseId: string | null;
  submissionId: string | null;
  updatedAt: string;
}

export interface MessageDto {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

export interface ConversationDetail {
  id: string;
  kind: "chat" | "submission_feedback";
  title: string | null;
  courseId: string | null;
  submissionId: string | null;
  draft: boolean;
  messages: MessageDto[];
}

export interface AvailabilityResponse {
  available: boolean;
  configured: boolean;
  /** `feature_off` | `unconfigured` | `over_quota` | null */
  reason: string | null;
  model: string;
  period: string;
  /** `<= 0` ⇒ no limit. */
  cap: number;
  used: number;
  /** `-1` ⇒ ilimitado. */
  remaining: number;
}

export interface ChatInput {
  org?: string;
  courseId?: string;
  lessonId?: string;
  cohortId?: string;
  conversationId?: string;
  message: string;
}

// ─── Operaciones ──────────────────────────────────────────────────────────────

const sendChat = (input: ChatInput): Promise<ChatResponse> =>
  apiJson<ChatResponse>(`${BASE}/chat`, { method: "POST", body: JSON.stringify(input) });

const requestSubmissionFeedback = (submissionId: string): Promise<ChatResponse> =>
  apiJson<ChatResponse>(`${BASE}/submission-feedback`, {
    method: "POST",
    body: JSON.stringify({ submissionId }),
  });

// ─── Query keys ────────────────────────────────────────────────────────────────

const aiTutorKeys = {
  conversations: () => ["ai-tutor", "conversations"] as const,
  conversation: (id: string) => ["ai-tutor", "conversation", id] as const,
  availability: (org?: string, courseId?: string) =>
    ["ai-tutor", "availability", org ?? "", courseId ?? ""] as const,
};

export const useConversations = () =>
  createSafeQuery(() => ({
    queryKey: aiTutorKeys.conversations(),
    queryFn: () => apiJson<ConversationSummary[]>(`${BASE}/conversations`),
    staleTime: 10_000,
  }));

export const useConversation = (id: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: aiTutorKeys.conversation(id() ?? ""),
    queryFn: () => apiJson<ConversationDetail>(`${BASE}/conversations/${id() ?? ""}`),
    get enabled() {
      return Boolean(id());
    },
  }));

export const useAvailability = (
  org: () => string | undefined,
  courseId: () => string | undefined,
) =>
  createSafeQuery(() => ({
    queryKey: aiTutorKeys.availability(org(), courseId()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      if (o) {
        params.set("org", o);
      }
      const c = courseId();
      if (c) {
        params.set("courseId", c);
      }
      const qs = params.toString();
      return apiJson<AvailabilityResponse>(`${BASE}/availability${qs ? `?${qs}` : ""}`);
    },
    staleTime: 30_000,
    retry: false,
  }));

export const useSendChat = () =>
  createMutation(() => ({
    mutationFn: (input: ChatInput) => sendChat(input),
  }));

export const useSubmissionFeedback = () =>
  createMutation(() => ({
    mutationFn: (submissionId: string) => requestSubmissionFeedback(submissionId),
  }));
